import { Bounds } from '~/util';
import type { Vec2 } from '~/util';

const SHAPE_RESOLUTION_X = 3;
const SHAPE_RESOLUTION_Y = 0.03;

/**
 * Calculate transformed SVG shape from specified point lists.
 *
 * The SVG shape resolution is limited in order to avoid subpixel points,
 * which improves the rendering speed of the shape for zoomed-out views.
 * Discontinuities between point lists are preserved, and are drawn as straight lines.
 *
 * The X coordinate values are transformed from unitBounds to drawBounds.
 * The Y coordinate values are scaled by timeScale.
 */
export function shapeFromPoints(
  points: Vec2[][],
  options: {
    unitBounds: Bounds;
    drawBounds: Bounds;
    timeScale: number;
    duration: number;
  }
) {
  const boundsSize = options.unitBounds.max - options.unitBounds.min;
  const yOffset = options.drawBounds.max;
  const height = Math.abs(options.drawBounds.max - options.drawBounds.min);

  function transformPoint(point: Vec2): Vec2 {
    const boundSpaceV =
      boundsSize == 0 ? 0 : (point.y - options.unitBounds.max) / -boundsSize;
    const viewSpaceV = yOffset + boundSpaceV * height;
    return {
      x: point.x / options.timeScale,
      y: viewSpaceV
    };
  }

  if (points.length === 0) {
    points = [
      [
        transformPoint({ x: 0, y: 0 }),
        transformPoint({ x: options.duration, y: 0 })
      ]
    ];
  }

  const overflowPad = 5 * options.timeScale;
  const outPointLeft = transformPoint({ x: -overflowPad, y: 0 });
  const outPointRight = transformPoint({
    x: options.duration + overflowPad,
    y: 0
  });

  const resolution_y = SHAPE_RESOLUTION_Y * boundsSize;

  let shape = `M ${outPointLeft.x},${outPointLeft.y} V${transformPoint(points[0][0]).y} `;
  for (let j = 0; j < points.length; j++) {
    let lastP = { x: -Infinity, y: Infinity };
    const inner = points[j];
    for (let i = 0; i < inner.length; i++) {
      const point = transformPoint(inner[i]);
      if (
        point.x - lastP.x >= SHAPE_RESOLUTION_X ||
        Math.abs(point.y - lastP.y) > resolution_y
      ) {
        lastP = point;
        shape += `L ${toFixedAtMostOne(point.x)},${toFixedAtMostOne(point.y)} `;
      }
    }
  }
  const lastInner = points[points.length - 1];
  const last = lastInner[lastInner.length - 1];
  shape += `L ${outPointRight.x},${transformPoint(last).y} V${outPointRight.y}`;
  return { shape, yZero: outPointLeft.y };
}

/** Print a value with variable 0 or 1 fixed precision, as needed. */
function toFixedAtMostOne(x: number) {
  return x - Math.floor(x) > 0.05 ? x.toFixed(1) : x.toFixed(0);
}

interface Tick {
  // value represented by the tick
  value: number;
  // position of tick
  pos: number;
}

/**
 * Find the tick labels and their positions for
 * specified value range and viewport box range.
 */
export function divisionPoints(
  valueBounds: Bounds,
  visualBounds: Bounds
): Tick[] {
  const naturalPixelsPerDiv = 20;
  const availableHeight = visualBounds.max - visualBounds.min;
  const boundsHeight = valueBounds.max - valueBounds.min;
  if (boundsHeight === 0 || availableHeight === 0) {
    return [];
  }
  const scale = Math.abs(availableHeight / boundsHeight);

  const legendStepSize = Math.max(1, Math.floor(naturalPixelsPerDiv / scale));
  const legendMin = Math.ceil(valueBounds.min / legendStepSize);
  const legendMax = Math.floor(valueBounds.max / legendStepSize);
  return Array.from({ length: legendMax - legendMin + 1 }, (_, i) => {
    const value = (legendMin + i) * legendStepSize;
    const f = (value - valueBounds.min) / boundsHeight;
    return {
      value,
      pos: (1 - f) * visualBounds.min + f * visualBounds.max
    };
  });
}

const padAbsolute = 4;

/**
 * Add some padding to the shape vertical bounds when there is space for it.
 */
function padBounds(rawValueBounds: Bounds, visualBounds: Bounds): Bounds {
  const visualHeight = Math.abs(visualBounds.max - visualBounds.min);
  const valueSpan = Math.abs(rawValueBounds.max - rawValueBounds.min);
  const availableHeight = visualHeight - padAbsolute * 2;
  if (availableHeight < 20) {
    return rawValueBounds;
  } else {
    const pad = (valueSpan / availableHeight) * padAbsolute;
    return {
      min: rawValueBounds.min - pad,
      max: rawValueBounds.max + pad
    };
  }
}

/**
 * Add some padding to the amplitude shape vertical bounds when there is space for it.
 */
export function amplitudeBounds(rawValueBounds: Bounds, visualBounds: Bounds) {
  return padBounds(
    Bounds.union({ min: 0, max: 1 }, rawValueBounds),
    visualBounds
  );
}

/**
 * Add some padding to the detuning shape vertical bounds when there is space for it.
 */
export function detuningBounds(rawValueBounds: Bounds, visualBounds: Bounds) {
  const rawBound = Math.max(
    1,
    Math.abs(rawValueBounds.min),
    Math.abs(rawValueBounds.max)
  );
  return padBounds({ min: -rawBound, max: rawBound }, visualBounds);
}
